import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AdjustmentsHorizontalIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import { farmAPI } from '../services/api';
import useHerdContextStore from '../store/herdContextStore';
import useUiStore from '../store/uiStore';
import { APP_TITLES } from '../constants/uiText';
import { formatJalaliDate } from '../utils/dateUtils';
import SearchableSelect from './SearchableSelect';
import { JalaliDateInput } from './JalaliDateInput';

function optionLabel(item) {
  if (!item) return '';
  const code = item.code ? `${item.code} — ` : '';
  return `${code}${item.name || item.title || item.id}`;
}

function HerdContextBar({ routeConfig }) {
  const navigate = useNavigate();
  const supportedFields = useMemo(() => new Set(routeConfig?.fields || []), [routeConfig]);
  const supports = useCallback((field) => supportedFields.has(field), [supportedFields]);
  const {
    siteId,
    unitId,
    baharbandId,
    scope,
    quickSearch,
    denseMode,
    dateContext,
    setSiteId,
    setUnitId,
    setBaharbandId,
    setScope,
    setQuickSearch,
    setDenseMode,
    setDateContext,
  } = useHerdContextStore();
  const setGlobalSearchOpen = useUiStore((state) => state.setGlobalSearchOpen);
  const expanded = useUiStore((state) => state.herdContextExpanded);
  const setExpanded = useUiStore((state) => state.setHerdContextExpanded);

  const [sites, setSites] = useState([]);
  const [units, setUnits] = useState([]);
  const [baharbands, setBaharbands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadErrors, setLoadErrors] = useState({ scope: false, units: false });

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      const needsSites = supports('site') || supports('unit');
      const needsBaharband = supports('baharband');
      if (!needsSites && !needsBaharband) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const [siteResult, baharbandResult] = await Promise.allSettled([
          needsSites ? farmAPI.getHerdSites({ page_size: 200 }) : Promise.resolve({ data: [] }),
          needsBaharband ? farmAPI.getBaharbands({ is_active: true, page_size: 200 }) : Promise.resolve({ data: [] }),
        ]);
        if (!mounted) return;
        const siteResp = siteResult.status === 'fulfilled' ? siteResult.value : { data: [] };
        const baharbandResp = baharbandResult.status === 'fulfilled' ? baharbandResult.value : { data: [] };
        setSites(siteResp.data?.results || siteResp.data || []);
        setBaharbands(baharbandResp.data?.results || baharbandResp.data || []);
        setLoadErrors((previous) => ({
          ...previous,
          scope: siteResult.status === 'rejected' || baharbandResult.status === 'rejected',
        }));
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, [supports]);

  useEffect(() => {
    let mounted = true;
    if (!supports('unit')) {
      setUnits([]);
      return () => { mounted = false; };
    }
    const loadUnits = async () => {
      try {
        const params = { page_size: 200 };
        if (siteId) params.site = siteId;
        const response = await farmAPI.getHerdUnits(params);
        if (!mounted) return;
        setUnits(response.data?.results || response.data || []);
        setLoadErrors((previous) => ({ ...previous, units: false }));
      } catch (error) {
        if (mounted) {
          setUnits([]);
          setLoadErrors((previous) => ({ ...previous, units: true }));
        }
      }
    };
    loadUnits();
    return () => {
      mounted = false;
    };
  }, [siteId, supports]);

  const selectedSite = useMemo(() => sites.find((site) => String(site.id) === String(siteId)), [siteId, sites]);
  const selectedUnit = useMemo(() => units.find((unit) => String(unit.id) === String(unitId)), [unitId, units]);
  const selectedBaharband = useMemo(() => baharbands.find((item) => String(item.id) === String(baharbandId)), [baharbands, baharbandId]);

  const activeFilterCount = [
    supports('site') && siteId,
    supports('unit') && unitId,
    supports('baharband') && baharbandId,
    supports('date') && dateContext,
    supports('scope') && scope === 'all' ? 'all' : '',
    supports('dense') && denseMode ? 'dense' : '',
  ].filter(Boolean).length;

  const submitQuickSearch = (event) => {
    event.preventDefault();
    const params = new URLSearchParams();
    if (quickSearch?.trim()) params.set('search', quickSearch.trim());
    if (supports('site') && siteId) params.set('site_id', siteId);
    if (supports('unit') && unitId) params.set('unit_id', unitId);
    if (supports('scope') && scope === 'all') params.set('scope', 'all');
    if (supports('baharband') && baharbandId) params.set('baharband', baharbandId);
    navigate(`/cows${params.toString() ? `?${params.toString()}` : ''}`);
  };

  return (
    <section className="no-print border-t border-slate-200 bg-slate-50/80" aria-label="فیلترهای گله">
      {(loadErrors.scope || loadErrors.units) ? (
        <div className="border-b border-amber-200 bg-amber-50 px-4 py-2 text-xs font-bold text-amber-800 lg:px-5">
          بخشی از فهرست سایت، واحد یا باهاربند بارگذاری نشد. صفحه قابل استفاده است، اما برای رفع ریشه خطا وضعیت Migrationهای سایت و واحد را بررسی کنید.
        </div>
      ) : null}
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2 lg:px-5">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-bold text-emerald-700">
            <CheckCircleIcon className="h-4 w-4" />
            فیلترهای نمایش‌داده‌شده روی این صفحه اعمال می‌شوند
          </span>
          <span className="hidden text-xs text-slate-500 xl:inline">{routeConfig?.description}</span>
          {supports('site') ? (
            <span className="rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-600">
              {selectedSite ? `سایت: ${selectedSite.name}` : 'همه سایت‌ها'}
            </span>
          ) : null}
          {supports('unit') ? (
            <span className="rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-600">
              {selectedUnit ? `واحد: ${selectedUnit.name}` : 'همه واحدها'}
            </span>
          ) : null}
          {supports('baharband') ? (
            <span className="rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-600">
              {selectedBaharband ? `${APP_TITLES.baharband}: ${selectedBaharband.code || selectedBaharband.name}` : `همه ${APP_TITLES.baharband}ها`}
            </span>
          ) : null}
          {supports('scope') ? (
            <span className="rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-600">
              {scope === 'active' ? APP_TITLES.activeOnly : APP_TITLES.allCows}
            </span>
          ) : null}
          {supports('date') && dateContext ? (
            <span className="rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-600">
              تاریخ مرجع: {formatJalaliDate(dateContext)}
            </span>
          ) : null}
        </div>

        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="inline-flex min-h-9 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-bold text-slate-700 transition hover:border-primary-200 hover:text-primary-700"
          aria-expanded={expanded}
          aria-controls="herd-context-controls"
        >
          <AdjustmentsHorizontalIcon className="h-4 w-4" />
          {expanded ? 'بستن فیلترها' : `تنظیم فیلترها${activeFilterCount ? ` (${activeFilterCount})` : ''}`}
          <ChevronDownIcon className={`h-4 w-4 transition-transform ${expanded ? 'rotate-180' : ''}`} />
        </button>
      </div>

      {expanded ? (
        <div id="herd-context-controls" className="border-t border-slate-200 bg-white px-4 py-3 lg:px-5">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
            {supports('site') ? (
              <div>
                <label className="label mb-1 text-xs">{APP_TITLES.site}</label>
                <SearchableSelect
                  value={siteId}
                  onChange={(event) => {
                    setSiteId(event.target.value);
                    setUnitId('');
                  }}
                  options={sites}
                  getOptionLabel={optionLabel}
                  placeholder="همه سایت‌ها"
                  emptyLabel="همه سایت‌ها"
                  searchPlaceholder="جست‌وجوی سایت..."
                  className="input py-2"
                  inputClassName="input py-2"
                  disabled={loading}
                />
              </div>
            ) : null}
            {supports('unit') ? (
              <div>
                <label className="label mb-1 text-xs">{APP_TITLES.unit}</label>
                <SearchableSelect
                  value={unitId}
                  onChange={(event) => setUnitId(event.target.value)}
                  options={units}
                  getOptionLabel={optionLabel}
                  placeholder="همه واحدها"
                  emptyLabel="همه واحدها"
                  searchPlaceholder="جست‌وجوی واحد..."
                  className="input py-2"
                  inputClassName="input py-2"
                  disabled={loading}
                />
              </div>
            ) : null}
            {supports('baharband') ? (
              <div>
                <label className="label mb-1 text-xs">{APP_TITLES.baharband}</label>
                <SearchableSelect
                  value={baharbandId}
                  onChange={(event) => setBaharbandId(event.target.value)}
                  options={baharbands}
                  getOptionLabel={optionLabel}
                  getOptionSearchText={(item) => `${item?.code || ''} ${item?.name || ''}`}
                  placeholder={`همه ${APP_TITLES.baharband}ها`}
                  emptyLabel={`همه ${APP_TITLES.baharband}ها`}
                  searchPlaceholder={`جست‌وجوی ${APP_TITLES.baharband}...`}
                  className="input py-2"
                  inputClassName="input py-2"
                  disabled={loading}
                />
              </div>
            ) : null}
            {supports('scope') ? (
              <div>
                <label className="label mb-1 text-xs">دام‌های نمایشی</label>
                <div className="flex min-h-11 items-center gap-1 rounded-lg border border-slate-200 bg-slate-50 p-1">
                  <button
                    type="button"
                    onClick={() => setScope('active')}
                    className={`flex-1 rounded-md px-2 py-2 text-xs font-bold transition ${scope === 'active' ? 'bg-primary-600 text-white shadow-sm' : 'text-slate-600 hover:bg-white'}`}
                  >
                    فعال
                  </button>
                  <button
                    type="button"
                    onClick={() => setScope('all')}
                    className={`flex-1 rounded-md px-2 py-2 text-xs font-bold transition ${scope === 'all' ? 'bg-primary-600 text-white shadow-sm' : 'text-slate-600 hover:bg-white'}`}
                  >
                    همه دام‌ها
                  </button>
                </div>
              </div>
            ) : null}
            {supports('date') ? (
              <div>
                <label className="label mb-1 text-xs">تاریخ مرجع</label>
                <JalaliDateInput
                  value={dateContext}
                  onChange={setDateContext}
                  placeholder="انتخاب تاریخ مرجع"
                  inputClass="input py-2"
                  clearable
                />
              </div>
            ) : null}
            {supports('dense') ? (
              <div>
                <label className="label mb-1 text-xs">نمایش جدول‌ها</label>
                <button
                  type="button"
                  className={`flex min-h-11 w-full items-center justify-center rounded-lg border px-3 py-2 text-sm font-bold transition ${denseMode ? 'border-primary-200 bg-primary-50 text-primary-700' : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'}`}
                  onClick={() => setDenseMode(!denseMode)}
                  aria-pressed={denseMode}
                >
                  {denseMode ? 'نمایش فشرده فعال است' : 'فعال‌کردن نمایش فشرده'}
                </button>
              </div>
            ) : null}
          </div>

          <form onSubmit={submitQuickSearch} className="mt-3 flex flex-col gap-2 rounded-xl border border-slate-200 bg-slate-50 p-2 sm:flex-row sm:items-center">
            <span className="px-1 text-xs font-bold text-slate-600">رفتن به پرونده دام:</span>
            <div className="relative flex-1">
              <MagnifyingGlassIcon className="pointer-events-none absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
              <input
                type="search"
                value={quickSearch}
                onChange={(event) => setQuickSearch(event.target.value)}
                className="input py-2.5 pr-10"
                placeholder="شماره گوش، شماره دامپزشکی، CHKNO، RNO یا نام"
              />
            </div>
            <button type="submit" className="btn btn-primary min-w-24">جست‌وجوی دام</button>
            <button type="button" className="btn btn-secondary" onClick={() => setGlobalSearchOpen(true)}>جست‌وجوی سراسری</button>
          </form>
        </div>
      ) : null}
    </section>
  );
}

export default HerdContextBar;
